import dayjs from "dayjs";
import {
  isAccept2Enabled,
  isCustomGoalExists,
  isEmail,
  isInvisible,
} from "./prompts/promptHelpers";
import { getTotals, percentage, getPeriodHeader, getPeriod, tally } from "./metricsHelpers";
import { toValue } from "vue";

export const saveToCsvFile = (contents, fname) => {
  const csvContent = contents.join("\n");
  const link = window.document.createElement("a");
  link.setAttribute(
    "href",
    `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(csvContent)}`,
  );
  const dateComponent = dayjs().format("MM_D_YYYY");
  link.setAttribute("download", `${dateComponent}_${fname}.csv`);
  link.click();
};

export const saveLinkToCsv = (url, fname) => {
  const link = window.document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${fname}.csv`);
  link.click();
};

export const getFiltersString = (filtersKeyData = {}) => {
  let filterString = "";
  if (filtersKeyData) {
    const filtersKeyDataValue = Object.values(toValue(filtersKeyData) ?? {});
    if (filtersKeyDataValue.length) {
      filtersKeyDataValue.forEach(value => {
        if (value.length) {
          filterString += `_${value.map(v => v.replace(/[^a-zA-Z0-9-]/g, "_")).join("_")}`;
        }
      });
    }
  }
  return filterString;
};

/**
 * Description
 * @param {Array<Object>} items
 * @param {('live-activities'|'live-errors')} [type=live-activities] - Object type.
 * @param {String} period
 * @param {Boolean} survey - If true, includes the survey option in the export.
 * @param {Object} [filtersKeyData={}] - The filters data object containing the filter values.
 * @return void
 */
export const exportLiveDataToCsv = (
  items = [],
  type = "live-activities",
  period,
  survey,
  filtersKeyData = {},
) => {
  let header = [];
  let values = [];
  if (type === "live-activities") {
    header = [
      "Timestamp",
      "User ID",
      "Activity",
      "Prompt Name",
      "Experiment Name",
      "Variation Name",
      "Language",
      "Device Category",
      "Segment Id",
      "Linked From User",
    ];
    if (survey) header.push("Survey Option");
    values = items.map(item => {
      const row = [
        `"${dayjs(item.timestamp, { format: "YYYYDDMMHHmm" }).format("MMM D, YYYY HH:mm:ss")}"`,
        `"${item.user_id}"`,
        `"${item.activity}"`,
        `"${item.prompt_name}"`,
        `"${item.experiment_name || ""}"`,
        `"${item.variation_name || ""}"`,
        `"${item.language || ""}"`,
        `"${item.device_category || ""}"`,
        `"${item.segment_id || ""}"`,
        `"${item.anon_linked_from_user_id || ""}"`,
      ];
      if (survey) row.push(`"${item.survey_option || ""}"`);
      return row;
    });
  }
  if (type === "live-errors") {
    header = ["Timestamp", "User ID", "Connector", "Error", "Prompt Name"];
    values = items.map(item => [
      `"${dayjs(item.timestamp, { format: "YYYYDDMMHHmm" }).format("MMM D, YYYY HH:mm:ss")}"`,
      `"${item.user_id}"`,
      `"${item.connector}"`,
      `"${item.error}"`,
      `"${item.prompt_name}"`,
    ]);
  }

  saveToCsvFile(
    [header, ...values],
    `live_${type}_${period}${getFiltersString(filtersKeyData)}`.trim(),
  );
};

export const exportSinglePromptToCsv = (prompt, period) => {
  const impressionsLabel = isEmail(prompt) ? "Sends" : "Impressions";
  const accept2Enabled = isAccept2Enabled(prompt);
  const hasCustomGoal = isCustomGoalExists(prompt);

  //! rewrite
  const statType = isInvisible(prompt) ? "goals" : "uimpressions";
  const totalUsers = prompt.data?.[statType]?.[period]?.uniques || 0;

  const totalGoals = getTotals(prompt.data, "goals", period);
  const totalGoals2 = getTotals(prompt.data, "goals2", period);
  const totalImpressions = getTotals(prompt.data, "impressions", period);
  const totalCustomGoals = getTotals(prompt.data, "custom_goals_seen", period) || 0;

  const CTRValue = (
    (totalImpressions ? percentage(totalGoals, totalImpressions) : 0) / 100
  ).toFixed(4);
  const displayConversionRateDefault = (
    (totalUsers ? percentage(totalGoals, totalUsers) : 0) / 100
  ).toFixed(4);
  const conversionRate = (
    (totalCustomGoals ? percentage(totalCustomGoals, totalUsers) : 0) / 100
  ).toFixed(4);
  const CTRAdditional = (
    (totalImpressions ? percentage(totalGoals2, totalImpressions) : 0) / 100
  ).toFixed(4);
  const conversionRateAdditionalValue = (
    (totalUsers ? percentage(totalGoals2, totalUsers) : 0) / 100
  ).toFixed(4);

  const headers = [
    "Users",
    impressionsLabel,
    "CTR",
    ...(accept2Enabled && !hasCustomGoal ? ["CTR (additional)"] : []),
    "Clicks",
    ...(hasCustomGoal ? ["Custom Goal"] : []),
    "Conv. Rate",
    ...(accept2Enabled && !hasCustomGoal ? ["Custom Goal (additional)"] : []),
  ];
  const values = [
    totalUsers,
    totalImpressions,
    CTRValue,
    ...(accept2Enabled && !hasCustomGoal ? [CTRAdditional] : []),
    totalGoals,
    ...(hasCustomGoal ? [totalCustomGoals] : []),
    ...(hasCustomGoal ? [conversionRate] : [displayConversionRateDefault]),
    ...(accept2Enabled && !hasCustomGoal ? [conversionRateAdditionalValue] : []),
  ];
  saveToCsvFile([headers, values], `prompt_${prompt.name}_${period}`);
};

export const exportMultipleSegmentsToCSV = (segments = [], period) => {
  const contents = [];
  const periods = getPeriodHeader(segments[0], "users", period);
  periods.forEach((date, dateIndex) => {
    const row = [date];
    segments.forEach(segment => {
      const periodRange = getPeriod(segment, "users", period);
      row.push(periodRange[dateIndex]);
    });
    contents.push(row);
  });

  const header = ["date", ...segments.map(item => item.name)];

  contents.unshift(header);
  saveToCsvFile(contents, `segments_${period}`);
};

export const exportMultipleGuidesToCSV = (guides = [], period) => {
  const contents = [];
  let hasGoal2 = false;
  guides.forEach(mainItem => {
    const items = mainItem.paths;
    items.forEach(item => {
      const segments = mainItem.segments.map(({ name }) => name).join(", ");
      const impressions = tally(item.data, period, "impressions");
      const users = tally(item.data, period, "uimpressions");
      const goals = tally(item.data, period, "goals");
      const ctr = percentage(goals, impressions, true);
      const convRate = percentage(goals, users, true);
      let row = [
        `"${mainItem.name}"`,
        `"${segments}"`,
        `"${item.name}"`,
        item.path_type,
        impressions,
        users,
        goals,
        ctr,
        convRate,
      ];

      if (item.data.goals2) {
        hasGoal2 = true;
        const goals2 = tally(item.data, period, "goals2");
        const ctr2 = percentage(goals2, impressions, true);

        const convRate2 = percentage(goals2, users, true);
        row = [...row, goals2, ctr2, convRate2];
      }
      contents.push(row);
    });
  });
  let header = [
    "guides",
    "segments",
    "prompt",
    "type",
    "impressions",
    "users",
    "clicks",
    "ctr",
    "conv. rate",
  ];
  if (hasGoal2) {
    header = [...header, "clicks 2", "ctr 2", "conv. rate 2"];
  }
  contents.unshift(header);

  saveToCsvFile(contents, `guides_${period}`);
};

/**
 * Description
 * @param {Array<Object>} [prompts=[]] items
 * @param {String} period
 * @param {Object} [filtersKeyData={}] - The filters data object containing the filter values.
 * @return void
 */
export const exportMultiplePromptToCsv = (prompts = [], period, filtersKeyData = {}) => {
  const promptsEmailAspect = prompts.reduce((acc, el) => acc + (isEmail(el) ? -1 : +1), 0);
  const impressionsLabel = !promptsEmailAspect
    ? "Sends/Impressions"
    : promptsEmailAspect < 0
      ? "Sends"
      : "Impressions";
  const accept2Enabled = !!prompts.find(isAccept2Enabled);
  const hasCustomGoal = !!prompts.find(isCustomGoalExists);

  const headers = [
    "Prompt name",
    "Tags",
    "Users",
    impressionsLabel,
    "CTR",
    ...(accept2Enabled && !hasCustomGoal ? ["CTR (additional)"] : []),
    "Clicks",
    ...(hasCustomGoal ? ["Custom Goal"] : []),
    "Conv. Rate",
    ...(accept2Enabled && !hasCustomGoal ? ["Custom Goal (additional)"] : []),
  ];

  const values = [];

  prompts.forEach(prompt => {
    const promptHasCustomGoal = isCustomGoalExists(prompt);
    const statType = isInvisible(prompt) ? "goals" : "uimpressions";
    const totalUsers = prompt.data?.[statType]?.[period]?.uniques || 0;

    const totalGoals = getTotals(prompt.data, "goals", period);
    const totalGoals2 = getTotals(prompt.data, "goals2", period);
    const totalImpressions = getTotals(prompt.data, "impressions", period);
    const totalCustomGoals = getTotals(prompt.data, "custom_goals_seen", period) || 0;

    const CTRValue = (
      (totalImpressions ? percentage(totalGoals, totalImpressions) : 0) / 100
    ).toFixed(4);
    const displayConversionRateDefault = (
      (totalUsers ? percentage(totalGoals, totalUsers) : 0) / 100
    ).toFixed(4);
    const conversionRate = (
      (totalCustomGoals ? percentage(totalCustomGoals, totalUsers) : 0) / 100
    ).toFixed(4);
    const CTRAdditional = (
      (totalImpressions ? percentage(totalGoals2, totalImpressions) : 0) / 100
    ).toFixed(4);
    const conversionRateAdditionalValue = (
      (totalUsers ? percentage(totalGoals2, totalUsers) : 0) / 100
    ).toFixed(4);

    const tagsLength = prompt.tags?.length || 0;
    values.push([
      `"${prompt.name}"`,
      tagsLength
        ? `"${prompt.tags.reduce(
            (acc, el, i) => `${acc} ${el.title}${i === tagsLength - 1 ? "" : ", "}`,
            "",
          )}"`
        : " ",
      totalUsers,
      totalImpressions,
      CTRValue,
      ...(accept2Enabled && !hasCustomGoal ? [CTRAdditional] : []),
      totalGoals,
      ...(hasCustomGoal ? [totalCustomGoals] : []),
      ...(promptHasCustomGoal ? [conversionRate] : [displayConversionRateDefault]),
      ...(accept2Enabled && !hasCustomGoal ? [conversionRateAdditionalValue] : []),
    ]);
  });

  saveToCsvFile(
    [headers, ...values],
    `prompt_${prompts.length}_${period}${getFiltersString(filtersKeyData)}`.trim(),
  );
};
